import React, { useLayoutEffect } from 'react';
import Depot from '../../../_shared/depot';
import { useContext, useEffect, useRef } from 'react';
import clsx from 'clsx';
import * as styles from './style.module.css';
import { DeviceManagerContext } from '@trainline-shareable/device-manager';
import { isClientSide, isDialogSupported } from '../helpers';
import { setMultipleRefs } from '../../../_shared/helpers';
// derived from https://html.spec.whatwg.org/multipage/dom.html#interactive-content excluding elements we are not going to use
// label isn't included because it moves focus to the assoiciated input rather than being focusable itself
const focusableElementsList = [
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'button:not([disabled])',
    '*[tabindex]',
];
const preventDefault = (event) => {
    event.preventDefault();
};
const HiddenFocusShiftElement = () => (React.createElement("div", { "data-focus-shift": "true", className: clsx(styles.focusShift), "aria-hidden": "true", tabIndex: 0 }));
const focusShiftSelector = '[data-focus-shift="true"]';
const focusableElementsQuery = focusableElementsList
    .map((element) => `${element}:not(${focusShiftSelector})`)
    .join(', ');
/** Notes on trapFocus:
 *
 * You may wonder why we implement a focus trap when the <dialog> element supposedly implements this...
 *
 * The reason is that whilst <dialog> makes the rest of the DOM inert and unfocusable,
 * moving focus forward from its last interactive element will allow focus to exit the given DOM.
 * i.e. focus moves to the browser window (url bar etc) or outside the inner iframe in the case of storybook
 *
 * This goes against a11y recommendations (see https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/)
 * which state focus should cycle within the dialog. (And yes, this means a11y has tensions with the w3c spec).
 *
 * The reason that trapFocus is only on conditionally applied is because when there are limited methods to close the modal
 * (see `dismissible = false` in modal/index.tsx), we don't want less able users to be able to end up stuck in the modal
 */
const DialogWithoutRef = ({ children, className, testId = 'dialog', isModal = true, isOpen, trapFocus, innerRef, ...commonProps }) => {
    const dialogRef = useRef(null);
    const deviceManager = useContext(DeviceManagerContext);
    const isMobileViewport = /(X?S)/.test((deviceManager === null || deviceManager === void 0 ? void 0 : deviceManager.viewPort) || 'S');
    const dialogClasses = clsx(styles.dialog, isMobileViewport ? styles.mobile : styles.desktop, className);
    useEffect(() => {
        var _a, _b, _c, _d;
        // we handle escape to close ourselves in either case
        (_a = dialogRef === null || dialogRef === void 0 ? void 0 : dialogRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('cancel', preventDefault);
        if (!isOpen) {
            (_b = dialogRef.current) === null || _b === void 0 ? void 0 : _b.close();
            return;
        }
        let localRef;
        if (dialogRef.current) {
            localRef = dialogRef;
        }
        if (isDialogSupported) {
            if (isModal) {
                (_c = dialogRef.current) === null || _c === void 0 ? void 0 : _c.showModal();
            }
            else {
                (_d = dialogRef.current) === null || _d === void 0 ? void 0 : _d.show();
            }
        }
        return () => {
            var _a;
            (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('cancel', preventDefault);
        };
    }, [isModal, isOpen]);
    useLayoutEffect(() => {
        let localRef;
        if (dialogRef.current) {
            localRef = dialogRef;
        }
        return () => {
            var _a;
            if (isDialogSupported)
                (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.close();
        };
    }, []);
    useEffect(() => {
        var _a, _b;
        if (!trapFocus || !isOpen) {
            return;
        }
        let focusableElements;
        let focusShiftElements;
        let firstElement;
        let lastElement;
        // this listens to `focusin` on the invisible <div data-focus-shift="true"> which bookend either side of the dialog
        // event.relatedTarget is the element focus is moving from
        const moveFocus = (event) => {
            // if the previously focused element was the first real element, the user is cycling backwards
            if (event.relatedTarget === firstElement) {
                return lastElement === null || lastElement === void 0 ? void 0 : lastElement.focus();
            }
            // if the previously focused element was the last real element, the user is cycling forwards
            if (event.relatedTarget === lastElement) {
                return firstElement === null || firstElement === void 0 ? void 0 : firstElement.focus();
            }
        };
        if (isClientSide) {
            focusableElements = (_a = dialogRef === null || dialogRef === void 0 ? void 0 : dialogRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(focusableElementsQuery);
            if (focusableElements === null || focusableElements === void 0 ? void 0 : focusableElements.length) {
                focusShiftElements =
                    (_b = dialogRef === null || dialogRef === void 0 ? void 0 : dialogRef.current) === null || _b === void 0 ? void 0 : _b.querySelectorAll(focusShiftSelector);
                // Note: if there is only 1 focusable element, these are the same (and moveFocus is unproblematic for that reason)
                firstElement = focusableElements === null || focusableElements === void 0 ? void 0 : focusableElements.item(0);
                lastElement = focusableElements === null || focusableElements === void 0 ? void 0 : focusableElements.item((focusableElements === null || focusableElements === void 0 ? void 0 : focusableElements.length) - 1);
                firstElement === null || firstElement === void 0 ? void 0 : firstElement.focus();
                focusShiftElements === null || focusShiftElements === void 0 ? void 0 : focusShiftElements.forEach((element) => {
                    element.addEventListener('focusin', moveFocus);
                });
            }
        }
        return () => {
            focusShiftElements === null || focusShiftElements === void 0 ? void 0 : focusShiftElements.forEach((element) => {
                element.removeEventListener('focusin', moveFocus);
            });
        };
    }, [trapFocus, isOpen]);
    return Depot.createElement('dialog', {
        className: dialogClasses,
        testId,
        innerRef: setMultipleRefs([dialogRef, innerRef]),
        ...commonProps,
    }, trapFocus ? (React.createElement(React.Fragment, null,
        React.createElement(HiddenFocusShiftElement, null),
        children,
        React.createElement(HiddenFocusShiftElement, null))) : (children));
};
const Dialog = Depot.withRef(DialogWithoutRef);
Dialog.displayName = 'Dialog';
export default Dialog;
//# sourceMappingURL=index.js.map